import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import FeatureIcon, { ICON_TONES, IconTone } from '../../components/FeatureIcon';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import MarketingScreenshot, { IOS_SNAPSHOT_BASE } from '../../components/MarketingScreenshot';
import { CAPTURE_BASES } from '../../lib/selectedCaptures';
import ContextualMedia from '../../components/ContextualMedia';

const AnimatedSection: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 1.2s ease-out, transform 1s ease-out'
            }
      }
    >
      {children}
    </div>
  );
};

const Privacy: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const altBand = theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)';

  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg leading-relaxed';

  const helpLink = (to: string, label: string) => (
    <Link
      to={to}
      className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
      style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
    >
      {label} →
    </Link>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Hero: founder voice, type-led */}
      <section className="pt-20 pb-14 md:pt-28 md:pb-20" style={{ backgroundColor: colors.surface }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
          <p className={eyebrow} style={{ color: dsColors.primary }}>Privacy &amp; Visibility</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-7 leading-[1.05]" style={{ color: heroPrimary, textWrap: 'balance' }}>
            Finding your crew shouldn't mean sharing everything.
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
            I wanted to know who I was riding with without putting every part of my life on display. So I built privacy around the moments that matter: your profile, joining an event, and taking part.
          </p>
        </div>
      </section>

      {/* Section 1 · Your profile and motorcycles */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            {/* Copy beside the principal visual; stacks on mobile. */}
            <ContextualMedia
              media={
                <MarketingScreenshot
                  base={CAPTURE_BASES.profilePrivacy}
                  alt="The Edit Privacy screen in RidePulse showing privacy presets, section controls, and location granularity"
                  caption="Profile visibility, example settings."
                />
              }
            >
              <div className="max-w-xl">
                <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Your profile and Garage</h2>
                <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Your profile helps other riders get a sense of who you are. You decide which parts of your riding story appear there, including your statistics, garage, and location.
                </p>
                <p className={`${sectionLead} mt-4`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Start with a privacy preset, then adjust individual sections. Show your city and state, show only your state, or keep your location hidden.
                </p>
                <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                  {helpLink('/social#profile-privacy', 'Compare the profile presets')}
                  {helpLink('/help#profile-preferences', 'Set up your profile visibility')}
                </div>
              </div>
            </ContextualMedia>

            <div className="mt-8">
              <ContextualMedia
                mediaSide="left"
                compactMedia
                media={
                  <MarketingScreenshot
                    base={CAPTURE_BASES.privacyDefaults}
                    alt="Privacy Options in RidePulse showing event participation visibility, name sharing with event hosts, and default motorcycle privacy controls"
                    caption="Garage and event defaults."
                  />
                }
              >
                <div>
                  <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                    In your Garage, choose whether additional motorcycles appear on your public profile: Public, Friends Only, or Hidden. Your primary motorcycle remains part of your profile's Garage display.
                  </p>
                  <p className={`${sectionLead} mt-4`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                    The motorcycle you select for an event appears on its participant roster, regardless of that bike's profile visibility.
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 max-w-4xl">
                    {[
                      ['On your profile', 'Hidden from your profile.', false],
                      ['At the event', 'Visible when you bring it to an event.', true],
                    ].map(([label, line, reveal]) => (
                      <div key={label as string} className="pt-4" style={{ borderTop: `1px solid ${cardBorder}` }}>
                        <dt className="text-xs font-bold uppercase tracking-widest" style={{ color: heroSecondary }}>{label}</dt>
                        <dd className="text-base font-bold mt-1" style={{ color: reveal ? dsColors[ICON_TONES.safety] : heroPrimary }}>{line}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                    {helpLink('/garage', 'Explore the Garage')}
                    {helpLink('/help#add-motorcycle', 'Understand motorcycle visibility')}
                  </div>
                </div>
              </ContextualMedia>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Section 2 · When you join an event */}
      <section className="py-16 md:py-24" style={{ backgroundColor: altBand }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <ContextualMedia
              media={
                <MarketingScreenshot
                  base={`${IOS_SNAPSHOT_BASE}/Event Details/iOS - Event Details - Join with Privacy Override`}
                  alt="Joining an event in RidePulse with a Public per-event privacy override, an unchanged Limited profile default, and separate phone and emergency-contact toggles"
                  caption="Choices for this event."
                />
              }
            >
              <div className="max-w-xl">
                <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Your choices for each event</h2>
                <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  Choose a privacy preference for an event without changing your profile default. Select the motorcycle you'll bring, then decide whether to share your phone number or emergency-contact information with the host. Those contact choices are separate and optional.
                </p>
                <p className="mt-6">{helpLink('/help#join-event', 'Choose what you share when joining')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-10">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: heroSecondary }}>Your usual preference</p>
                    <p className="text-xl sm:text-2xl font-bold leading-snug" style={{ color: heroPrimary, textWrap: 'balance' }}>Profile default: unchanged</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: dsColors.primary }}>This event's preference</p>
                    <p className="text-xl sm:text-2xl font-bold leading-snug" style={{ color: dsColors.primary, textWrap: 'balance' }}>Event preference: applies here</p>
                  </div>
                </div>
                <div className="mt-8">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: heroSecondary }}>Optional contact sharing</p>
                  <div className="flex flex-wrap gap-2">
                    {['Phone number', 'Emergency contact'].map((c) => (
                      <span key={c} className="text-sm font-semibold px-3.5 py-1.5 rounded-full" style={{ border: `1px solid ${cardBorder}`, color: heroPrimary }}>{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            </ContextualMedia>

            <div className="mt-16 md:mt-20 max-w-4xl">
              <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                For ride events, the meetup is visible first. The destination and waypoints are revealed at check-in.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
                <div className="rounded-2xl p-5" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: heroSecondary }}>Before check-in</p>
                  <div className="flex items-center gap-2 my-4" aria-hidden="true">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: heroPrimary }} />
                    <span className="flex-1 border-t-2 border-dashed" style={{ borderColor: cardBorder }} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: heroSecondary, flexShrink: 0 }}>
                      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </g>
                    </svg>
                    <span className="flex-1 border-t-2 border-dashed" style={{ borderColor: cardBorder }} />
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ border: `2px solid ${cardBorder}` }} />
                  </div>
                  <div className="flex justify-between text-xs mb-3" style={{ color: heroSecondary }} aria-hidden="true">
                    <span>Meetup</span><span>Destination</span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: heroSecondary }}>
                    The meetup is visible. Destination and stops stay locked.
                  </p>
                </div>
                <div className="rounded-2xl p-5" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: dsColors[ICON_TONES.safety] }}>After check-in</p>
                  <div className="relative my-4" aria-hidden="true">
                    <div className="absolute left-0 right-0 border-t-2" style={{ borderColor: dsColors[ICON_TONES.safety], top: '5px' }} />
                    <div className="relative grid grid-cols-5">
                      {([
                        { label: 'Meetup', size: 'w-3 h-3', dot: { backgroundColor: dsColors[ICON_TONES.safety] } },
                        { label: 'Fuel', size: 'w-1.5 h-1.5', dot: { backgroundColor: dsColors[ICON_TONES.safety] } },
                        { label: 'Rest', size: 'w-1.5 h-1.5', dot: { backgroundColor: dsColors[ICON_TONES.safety] } },
                        { label: 'Photo', size: 'w-1.5 h-1.5', dot: { backgroundColor: dsColors[ICON_TONES.safety] } },
                        { label: 'Destination', size: 'w-3 h-3', dot: { border: `2px solid ${dsColors[ICON_TONES.safety]}`, backgroundColor: colors.surface } },
                      ] as { label: string; size: string; dot: React.CSSProperties }[]).map((s) => (
                        <div key={s.label} className="flex flex-col items-center">
                          <span className={`rounded-full flex-shrink-0 ${s.size}`} style={s.dot} />
                          <span className="mt-2 text-xs" style={{ color: heroSecondary }}>{s.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm mt-4" style={{ color: heroSecondary }}>
                Meetup-style event categories have no route to reveal.
              </p>
              <p className="mt-6">{helpLink('/help#check-in', 'See when route details become available')}</p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Section 3 · Live location sharing */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="max-w-3xl">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>GroupPulse location sharing</h2>
              <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                With GroupPulse, choose who sees your live location during the ride: the group, the host and co-hosts, or neither. You can change that choice using the on-screen controls.
              </p>
              <p className={`${sectionLead} mt-4`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                It's a choice for the ride, not a standing permission. Changes apply going forward and cannot take back information someone has already seen. Sharing ends when the ride ends.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
              {[
                {
                  name: 'Group',
                  desc: 'Riders taking part in the event see your live position.',
                  audience: <FeatureIcon name="users" size={26} />,
                  tone: 'community' as IconTone,
                },
                {
                  name: 'Host and co-hosts',
                  desc: 'The host and co-hosts running the ride see your live position.',
                  audience: (
                    <span className="inline-flex items-center gap-2">
                      <FeatureIcon name="star" size={22} />
                      <FeatureIcon name="users" size={20} />
                    </span>
                  ),
                  tone: 'primary' as IconTone,
                },
                {
                  name: 'Location not shared',
                  desc: 'Your live position stays off the group map.',
                  audience: (
                    <span className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full" style={{ border: `1px solid ${cardBorder}` }}>
                      Off
                    </span>
                  ),
                  tone: 'neutral' as IconTone,
                },
              ].map((c) => (
                <div key={c.name} className="rounded-2xl p-5" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-4">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: heroPrimary }} aria-hidden="true" />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: heroSecondary }}>You</span>
                    <span aria-hidden="true" style={{ color: heroSecondary }}>→</span>
                    <span style={{ color: dsColors[ICON_TONES[c.tone]] }}>{c.audience}</span>
                  </div>
                  <p className="text-sm font-bold mb-1.5" style={{ color: heroPrimary }}>{c.name}</p>
                  <p className="text-sm leading-relaxed" style={{ color: heroSecondary }}>{c.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 max-w-3xl rounded-2xl p-4" style={{ border: `1px solid ${cardBorder}` }}>
              <span style={{ color: dsColors[ICON_TONES.attention], flexShrink: 0, marginTop: '1px' }} aria-hidden="true">
                <FeatureIcon name="shield" size={18} />
              </span>
              <p className="text-sm leading-relaxed" style={{ color: heroSecondary }}>
                <span className="font-semibold" style={{ color: heroPrimary }}>One exception:</span> sending an SOS shares your location for emergency help, even when your location is not shared with the group.{' '}
                <Link
                  to="/help#grouppulse"
                  className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                  style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                >
                  How SOS works →
                </Link>
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {helpLink('/grouppulse', 'Explore GroupPulse')}
              {helpLink('/help#grouppulse', 'Location sharing in Help')}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Close: why blocking and reporting exist, then the onward links */}
      <section className="py-14 md:py-20" style={{ backgroundColor: altBand }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
              A community you want to be part of.
            </h2>
            <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Not every interaction is a good fit. Blocking and reporting give you options when an interaction is unwanted or something needs attention.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
              {helpLink('/help#blocking-reporting', 'Blocking & reporting')}
              {helpLink('/help#host-chat', 'Event chat & retention')}
              {helpLink('/help', 'Guides & Help')}
              {helpLink('/legal/privacy', 'Privacy Policy')}
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;
